/**
 * `/admin/agents/prompts/:id` — single-prompt detail page (#574 PR 3).
 *
 * Moved from `/admin/prompts/:id` as part of the SuperAdmin → Agents
 * sub-nav reshape; the old URL still resolves via the redirect in
 * `App.tsx`.
 *
 * Layout (matches docs/mockups/prompt-management.html):
 *   - Header strip: name, type pill, description (inline display),
 *     editable tag chips
 *   - Two-column body:
 *       Versions rail (left, 280px): version history newest-first;
 *       current row marked, hover-revealed Activate button on
 *       non-current rows
 *       Body view (right): in read mode, Monaco viewer of current
 *       (or DiffPane of current↔selected); in edit mode, Monaco
 *       editor of current with Save button → SaveVersionDialog
 *   - Comments thread: below the body view, simple author/text list
 *     with an Add form
 *
 * Reuses MonacoCodePane + DiffPane from pages/Tests/components/ —
 * they're framework-agnostic wrappers around Monaco that the script-
 * history surface (#546) already proved out.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
} from '@/components/ui'
import {
  adminPromptsAPI,
  type AdminPromptDetail,
  type AdminPromptVersion,
  type AdminPromptType,
} from '@/lib/api'
import { MonacoCodePane } from '@/pages/Tests/components/MonacoCodePane'
import { DiffPane } from '@/pages/Tests/components/DiffPane'
import { SaveVersionDialog } from './SaveVersionDialog'
import { ActivateConfirmDialog } from './ActivateConfirmDialog'
import { useConfirm } from '@/hooks/useConfirm'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate, formatLocalDateTime } from '@/lib/utils'

const TYPE_LABEL: Record<AdminPromptType, string> = {
  planner: 'Planner', generator: 'Generator', healer: 'Healer',
  analyzer: 'Analyzer', auditor: 'Auditor', scanner: 'Scanner',
  proposer: 'Proposer', refiner: 'Refiner', dedupJudge: 'Deduper', shared: 'Shared rules',
}

const TYPE_PILL: Record<AdminPromptType, string> = {
  planner:   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  generator: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  healer:    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  analyzer:  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  auditor:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  scanner:   'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
  // Pink — distinct from the agent-loop palette; proposer is a
  // structured-output extractor, not a multi-turn agent.
  proposer:  'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200',
  // Rose — the proposer's second pass, one hue over so they read as related.
  refiner:   'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  // Indigo — the Deduper (#2309), a distinct hue from the agent-loop palette.
  dedupJudge:'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  // Slate — deliberately outside the per-agent palette, because a
  // `shared` row is NOT an agent: it is a rules chunk more than one
  // agent composes, so colouring it like one would misread (#1901).
  shared:    'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
}

export function PromptDetailPage() {
  const { id } = useParams<{ id: string }>()
  const tz = useUserTimezone()
  // #1215: gate the per-comment Delete affordance behind a confirm so
  // a misclick doesn't silently drop a thread.
  const confirm = useConfirm()
  const [data, setData] = useState<AdminPromptDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [draftBody, setDraftBody] = useState('')

  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activateTarget, setActivateTarget] = useState<AdminPromptVersion | null>(null)
  const [activating, setActivating] = useState(false)

  const [newTag, setNewTag] = useState('')
  const [tagSaving, setTagSaving] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentPosting, setCommentPosting] = useState(false)
  const [opError, setOpError] = useState<string | null>(null)

  async function reload(): Promise<void> {
    if (!id) return
    setLoading(true)
    try {
      const d = await adminPromptsAPI.get(id)
      setData(d)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    setSelectedVersionId(null)
    setEditMode(false)
  }, [id])

  // Active version is the one Prompt.activeVersionId points to.
  const activeVersion = useMemo(() => {
    if (!data) return null
    return data.versions.find(v => v._id === data.prompt.activeVersionId) ?? null
  }, [data])

  const selectedVersion = useMemo(() => {
    if (!data || !selectedVersionId) return null
    return data.versions.find(v => v._id === selectedVersionId) ?? null
  }, [data, selectedVersionId])

  // Seed the edit draft with the current active body whenever we
  // enter edit mode or the active version changes underneath.
  useEffect(() => {
    if (editMode) setDraftBody(activeVersion?.body ?? '')
  }, [editMode, activeVersion])

  async function handleSaveVersion(input: { commitMessage: string; details?: string; publish: boolean }): Promise<void> {
    if (!id) return
    // Defense-in-depth: the Save button is already disabled when
    // the draft is blank, but a stale state or programmatic call
    // shouldn't be able to write an empty body. Same trim semantics
    // the button's disabled check uses.
    if (draftBody.trim().length === 0) {
      setOpError('Body cannot be empty.')
      return
    }
    setSaving(true)
    setOpError(null)
    try {
      const fullMessage = input.details ? `${input.commitMessage}\n\n${input.details}` : input.commitMessage
      const newVersion = await adminPromptsAPI.createVersion(id, {
        body: draftBody,
        commitMessage: fullMessage,
      })
      if (input.publish) {
        await adminPromptsAPI.activate(id, newVersion._id)
      }
      setSaveDialogOpen(false)
      setEditMode(false)
      setSelectedVersionId(null)
      await reload()
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleActivate(): Promise<void> {
    if (!id || !activateTarget) return
    setActivating(true)
    setOpError(null)
    try {
      await adminPromptsAPI.activate(id, activateTarget._id)
      setActivateTarget(null)
      setSelectedVersionId(null)
      await reload()
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Activate failed')
    } finally {
      setActivating(false)
    }
  }

  async function handleAddTag(): Promise<void> {
    if (!id || !data) return
    const t = newTag.trim()
    if (!t) return
    setTagSaving(true)
    setOpError(null)
    try {
      const next = [...data.prompt.tags, t]
      const updated = await adminPromptsAPI.updateTags(id, next)
      setData({ ...data, prompt: updated })
      setNewTag('')
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Tag update failed')
    } finally {
      setTagSaving(false)
    }
  }

  async function handleRemoveTag(tag: string): Promise<void> {
    if (!id || !data) return
    setTagSaving(true)
    setOpError(null)
    try {
      const next = data.prompt.tags.filter(t => t !== tag)
      const updated = await adminPromptsAPI.updateTags(id, next)
      setData({ ...data, prompt: updated })
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Tag update failed')
    } finally {
      setTagSaving(false)
    }
  }

  async function handleAddComment(): Promise<void> {
    if (!id || !data) return
    const text = newComment.trim()
    if (!text) return
    setCommentPosting(true)
    setOpError(null)
    try {
      const c = await adminPromptsAPI.addComment(id, text)
      setData({ ...data, prompt: { ...data.prompt, comments: [...data.prompt.comments, c] } })
      setNewComment('')
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Comment post failed')
    } finally {
      setCommentPosting(false)
    }
  }

  async function handleRemoveComment(commentId: string): Promise<void> {
    if (!id || !data) return
    const ok = await confirm({
      title: 'Delete this comment?',
      description: 'The comment is permanently removed from the thread.',
      confirmLabel: 'Delete comment',
      destructive: true,
    })
    if (!ok) return
    setOpError(null)
    try {
      await adminPromptsAPI.removeComment(id, commentId)
      setData({
        ...data,
        prompt: { ...data.prompt, comments: data.prompt.comments.filter(c => c.id !== commentId) },
      })
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Comment delete failed')
    }
  }

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading prompt…</p>
  }
  if (loadError && !data) {
    return <p className="text-sm text-destructive" role="alert">{loadError}</p>
  }
  if (!data) return null

  const { prompt, versions } = data

  return (
    <div className="space-y-4 max-w-full">
      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">Global Admin</Link>
        {' · '}
        <Link to="/admin/models" className="hover:text-foreground">AI Models</Link>
        {' · '}
        <Link to="/admin/models/prompts" className="hover:text-foreground">Prompts</Link>
        {' · '}
        <span className="font-mono">{prompt.name}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold font-mono flex items-center gap-2">
            {prompt.name}
            <span className={`text-xs font-sans font-semibold px-2 py-0.5 rounded ${TYPE_PILL[prompt.type]}`}>
              {TYPE_LABEL[prompt.type]}
            </span>
          </h1>
          {prompt.description && (
            <p className="text-sm text-muted-foreground mt-1">{prompt.description}</p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {!editMode && (
            <Button onClick={() => { setEditMode(true); setSelectedVersionId(null) }}>
              Edit body
            </Button>
          )}
          {editMode && (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)} disabled={saving}>Cancel</Button>
              <Button
                onClick={() => setSaveDialogOpen(true)}
                disabled={
                  saving
                  || draftBody === (activeVersion?.body ?? '')
                  || draftBody.trim().length === 0
                }
              >
                Save new version
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Tags</span>
        {prompt.tags.length === 0 && (
          <span className="text-xs text-muted-foreground italic">none</span>
        )}
        {prompt.tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-muted">
            {t}
            <button
              type="button"
              onClick={() => void handleRemoveTag(t)}
              disabled={tagSaving}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Remove tag ${t}`}
            >×</button>
          </span>
        ))}
        <Input
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void handleAddTag() }}
          placeholder="+ add tag"
          className="h-7 w-32 text-xs"
        />
      </div>

      {opError && (
        <p className="text-sm text-destructive" role="alert">{opError}</p>
      )}

      <div className="grid grid-cols-[280px_1fr] gap-4 min-h-[480px]">
        {/* Versions rail */}
        <Card className="overflow-hidden flex flex-col">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Versions ({versions.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-2 space-y-1">
            {versions.length === 0 && (
              <p className="text-xs text-muted-foreground italic p-2">No versions yet.</p>
            )}
            {versions.map(v => {
              const isCurrent = v._id === prompt.activeVersionId
              const isSelected = v._id === selectedVersionId
              return (
                <div key={v._id} className="relative group">
                  <button
                    type="button"
                    onClick={() => {
                      if (editMode) return
                      setSelectedVersionId(prev => prev === v._id ? null : v._id)
                    }}
                    aria-pressed={isSelected}
                    aria-current={isCurrent ? 'true' : undefined}
                    disabled={editMode}
                    className={`w-full text-left px-2 py-2 rounded-md border-2 ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : isCurrent
                          ? 'border-emerald-500/40 bg-emerald-500/5'
                          : 'border-transparent hover:bg-muted'
                    } ${editMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-mono font-semibold">v{v.version}</span>
                      <span className="text-xs flex-1 truncate" title={v.commitMessage}>
                        {v.commitMessage.split('\n')[0]}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                      <span>{formatDate(v.createdAt, tz)}</span>
                      {isCurrent && <span className="text-emerald-600 dark:text-emerald-400">· active</span>}
                      {!isCurrent && v.activatedAt && v.expiredAt && (
                        <span>· active {formatDate(v.activatedAt, tz)} → {formatDate(v.expiredAt, tz)}</span>
                      )}
                      {!isCurrent && !v.activatedAt && (
                        <span className="italic">· never active</span>
                      )}
                    </div>
                  </button>
                  {!isCurrent && !editMode && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setActivateTarget(v) }}
                      aria-label={`Activate v${v.version}`}
                      className={`absolute right-2 bottom-2 text-[10px] px-2 py-0.5 rounded border bg-background hover:bg-muted ${
                        isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      Activate
                    </button>
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>

        {/* Body view */}
        <div className="border rounded-md overflow-hidden bg-card flex flex-col min-h-[480px]">
          <div className="px-3 py-1 border-b text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-2">
            <span>
              {editMode ? 'Editing body' : selectedVersion ? `Comparing v${selectedVersion.version} ↔ v${activeVersion?.version ?? '?'} (current)` : 'Active body'}
              {activeVersion && !editMode && !selectedVersion && (
                <span className="ml-2 font-normal normal-case text-foreground/80">v{activeVersion.version}</span>
              )}
            </span>
          </div>
          <div className="flex-1 min-h-0">
            {editMode ? (
              <MonacoCodePane
                value={draftBody}
                onChange={setDraftBody}
                readOnly={false}
                language="markdown"
              />
            ) : selectedVersion && activeVersion ? (
              <DiffPane
                original={activeVersion.body}
                modified={selectedVersion.body}
                originalLabel={`v${activeVersion.version} · current`}
                modifiedLabel={`v${selectedVersion.version} · selected`}
                language="markdown"
              />
            ) : activeVersion ? (
              <MonacoCodePane value={activeVersion.body} readOnly language="markdown" />
            ) : (
              <p className="text-sm text-muted-foreground italic p-4">No active version.</p>
            )}
          </div>
        </div>
      </div>

      {/* Comments */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">
            Comments ({prompt.comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {prompt.comments.length === 0 && (
            <p className="text-xs text-muted-foreground italic">No comments yet.</p>
          )}
          {prompt.comments.map(c => (
            <div key={c.id} className="border rounded-md p-3 bg-muted/20 group">
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="font-mono text-[10px]">{c.authorUserId.slice(0, 8)}</Badge>
                <span className="text-muted-foreground">{formatLocalDateTime(c.createdAt, tz)}</span>
                <button
                  type="button"
                  onClick={() => void handleRemoveComment(c.id)}
                  className="ml-auto text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
                  aria-label="Delete comment"
                >Delete</button>
              </div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
          <div className="flex gap-2 items-start pt-2 border-t">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment…"
              rows={2}
              className="flex-1 text-sm"
            />
            <Button
              onClick={() => void handleAddComment()}
              disabled={commentPosting || !newComment.trim()}
            >
              Post
            </Button>
          </div>
        </CardContent>
      </Card>

      <SaveVersionDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        nextVersionPreview={prompt.nextVersion}
        onSubmit={handleSaveVersion}
        saving={saving}
      />
      <ActivateConfirmDialog
        open={activateTarget !== null}
        onOpenChange={(o) => !activating && !o && setActivateTarget(null)}
        target={activateTarget}
        currentVersion={activeVersion?.version ?? null}
        onConfirm={handleActivate}
        activating={activating}
      />

    </div>
  )
}
